// « Coupon » on the admin order page (2026-10-28). Shows the order's coupon if
// it has one; « Appliquer un coupon » lets staff put a code a client sent on
// WhatsApp onto the order (money changes stay human). Calls the ADMIN variant
// of POST /orders/:id/coupon; a refusal is shown in French.
// Renders nothing unless codes are accepted (coupon_redeem_enabled) or the
// order already carries a coupon.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { couponsApi, settingsApi, type Order } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import {
  showOrderCouponBlock, couponErrorMessage, normalizeCodeInput,
} from '@/pages/admin/coupons/couponMath';

export function OrderCouponCard({ order, shopifyOrderId }: { order: Order; shopifyOrderId: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const { data: settingsData } = useQuery({ queryKey: ['settings'], queryFn: () => settingsApi.get() });
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['order', shopifyOrderId] });
    qc.invalidateQueries({ queryKey: ['order-pricing', shopifyOrderId] });
    qc.invalidateQueries({ queryKey: ['admin-orders'] });
  };

  const apply = useMutation({
    mutationFn: (c: string) => couponsApi.applyToOrder(shopifyOrderId, c),
    onSuccess: (r) => {
      if (r && r.ok === false) { setMsg({ ok: false, text: t(couponErrorMessage(r)) }); return; }
      setMsg({ ok: true, text: t('Coupon appliqué : −{n} DH', { n: Math.round(Number(r?.coupon_discount ?? 0)) }) });
      setCode('');
      refresh();
    },
    onError: (e) => setMsg({ ok: false, text: t(couponErrorMessage(e)) }),
  });

  const remove = useMutation({
    mutationFn: () => couponsApi.removeFromOrder(shopifyOrderId),
    onSuccess: (r) => {
      if (r && r.ok === false) { setMsg({ ok: false, text: t(couponErrorMessage(r)) }); return; }
      setMsg({ ok: true, text: t('Coupon retiré.') });
      refresh();
    },
    onError: (e) => setMsg({ ok: false, text: t(couponErrorMessage(e)) }),
  });

  if (!showOrderCouponBlock(settingsData?.settings, order)) return null;

  const has = !!order.coupon_code;
  const discount = Math.round(Number(order.coupon_discount ?? 0));
  const submit = () => {
    const c = normalizeCodeInput(code);
    if (!c) return;
    setMsg(null);
    apply.mutate(c);
  };

  return (
    <div className="rounded-xl border border-border-subtle bg-bg-card p-4 space-y-3">
      <p className="text-[12px] font-semibold text-text-secondary">🎟 {t('Coupon')}</p>
      {has ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-text-primary break-all">{order.coupon_code}</span>
          {discount > 0 && <span className="text-sm font-semibold text-status-done">−{discount} DH</span>}
          <button type="button" onClick={() => { setMsg(null); remove.mutate(); }} disabled={remove.isPending}
            className="ml-auto text-xs font-semibold text-status-stuck min-h-11 md:min-h-0 disabled:opacity-50">
            {remove.isPending ? t('Retrait…') : t('Retirer')}
          </button>
        </div>
      ) : (
        <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t('Code reçu sur WhatsApp')}
            aria-label={t('Appliquer un coupon')}
            autoCapitalize="characters" autoCorrect="off" spellCheck={false}
            className="flex-1 min-w-0 h-11 px-3 rounded-xl border border-border-default bg-bg-card font-mono text-base md:text-sm text-text-primary uppercase"
          />
          <Button type="submit" size="sm" loading={apply.isPending} disabled={!normalizeCodeInput(code)}>
            {t('Appliquer')}
          </Button>
        </form>
      )}
      {!has && <p className="text-[11px] text-text-tertiary">{t('Appliquer un coupon : le total de la commande baisse du montant du code.')}</p>}
      {msg && (
        <p role={msg.ok ? 'status' : 'alert'} className={`text-[12px] font-semibold ${msg.ok ? 'text-status-done' : 'text-status-stuck'}`}>
          {msg.ok ? '✓ ' : ''}{msg.text}
        </p>
      )}
    </div>
  );
}
